import React, { useState, useEffect, useRef, useCallback } from "react";
import { HighlightedMatch } from "@magnetic/highlighted-match";
import { Text, Search, Flex, MSpinner as Spinner } from "@cisco/spaces-dash-lib-styleguide";
import { CustomerItem } from "../../../types/components";
import "./dropdown-simple.component.css";

interface DropdownSimpleProps {
  listItems?: CustomerItem[];
  onDropdownSelect?: (tenantId: number) => void;
}

function DropdownSimple({ listItems = [], onDropdownSelect }: DropdownSimpleProps): JSX.Element {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [dropdownExpand, setDropdownExpand] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredList, setFilteredList] = useState<CustomerItem[]>([]);
  const [isLoadingList, setLoadingList] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const loadMoreItems = (list: CustomerItem[] = []) => {
    console.log("Load more items triggered..", hasMore, list);
    if (!hasMore) return;
    console.log("Load more items triggered process fetching new items..");

    setTimeout(() => {
      let filteredData: CustomerItem[] = [];
      if (searchText) {
        filteredData = [...list].filter((item) => {
          const { customerName, accountNumber, tenantId } = item;
          return (
            (customerName && customerName.toLowerCase().includes(searchText)) ||
            (accountNumber && accountNumber.toLowerCase().includes(searchText)) ||
            (tenantId && tenantId.toString().includes(searchText))
          );
        });
      } else {
        filteredData = [...list];
      }
      const updatedList = filteredData.slice(0, (filteredList?.length ?? 0) + 50);
      if (filteredData?.length === (filteredList?.length ?? 0) + 50) {
        console.log("Load more has more toggled");
        setHasMore(false);
      }
      setFilteredList(() => updatedList);
    }, 1000);
  };

  const handleScroll = () => {
    console.log("Load more Scroll event", hasMore, listRef.current);
    if (!listRef.current || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom < 200) {
      console.log("Load more Scroll triggered");
      loadMoreItems(listItems);
    }
  };

  useEffect(() => {
    const container = listRef.current;
    if (listItems?.length > 0 && container) {
      container.removeEventListener("scroll", handleScroll);
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [hasMore, listItems, filteredList]);

  useEffect(() => {
    if (listItems?.length > 100) {
      setFilteredList(() => [...listItems]?.slice(0, 100) || []);
    } else {
      setFilteredList(() => listItems || []);
    }
  }, [listItems]);

  const expandDropdown = useCallback(() => {
    setDropdownExpand((prev) => !prev);
  }, []);

  const selectItem = useCallback(
    (tenantId: number, customerName: string) => {
      setSelectedItem(customerName);
      setSearchText(customerName);
      expandDropdown();
      if (onDropdownSelect) {
        onDropdownSelect(tenantId);
      }
      setDropdownExpand(false);
    },
    [onDropdownSelect, expandDropdown]
  );

  const handleSearchThrottle = (event: any) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
    setLoadingList(true);
  };

  const onClearSearch = () => {
    setSearchText("");
    setDropdownExpand(false);
  };

  useEffect(() => {
    setFilteredList(() => []);
    const timeoutId = setTimeout(() => {
      const filteredData = [...listItems].filter((item) => {
        const { customerName, accountNumber, tenantId } = item;
        return (
          (customerName && customerName.toLowerCase().includes(searchText)) ||
          (accountNumber && accountNumber.toLowerCase().includes(searchText)) ||
          (tenantId && tenantId.toString().includes(searchText))
        );
      });

      if (filteredData?.length > 100) {
        setFilteredList(() => filteredData?.slice(0, 100));
      } else {
        setFilteredList(() => filteredData || []);
      }
      setLoadingList(false);
    }, 400);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText, listItems]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      action();
      event.preventDefault();
    }
  }, []);

  const filteredListElement = (list: CustomerItem[]) => {
    const listitems = list?.map((item) => (
      <div
        key={item.tenantId}
        className="eachElement"
        onClick={() => selectItem(item.tenantId, item.customerName)}
        onKeyDown={(e) => handleKeyDown(e, () => selectItem(item.tenantId, item.customerName))}
        role="option"
        tabIndex={0}
        aria-selected={selectedItem === item.customerName}
      >
        <Text size="p2">
          <HighlightedMatch searchTerm={searchText}>{item?.customerName}</HighlightedMatch>
        </Text>
        <Text>
          Account ID: <HighlightedMatch searchTerm={searchText}>{item.accountNumber || ""}</HighlightedMatch> | Tenant ID:{" "}
          <HighlightedMatch searchTerm={searchText}>{item?.tenantId.toString() || ""}</HighlightedMatch> | Status:
          <Text style={{ color: item.status === "Active" ? "green" : "red", display: "inline" }}>{item.status}</Text>
        </Text>
      </div>
    ));
    return [...listitems];
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownExpand(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const infoText = () =>
    isLoadingList ? (
      <Flex align="center" gap={12} justify="center" style={{ height: 200 }}>
        <Spinner size="sm" />
        <Text color="light" size="p2" weight="semi-bold">
          Loading
        </Text>
      </Flex>
    ) : (
      <Flex justify="center" align="center" style={{ height: 200, background: "var(--control-bg-weak)" }}>
        <Text>No Matches Found</Text>
      </Flex>
    );

  return (
    <div className={`simpleDropdownContainer ${dropdownExpand ? "dropdownExpand" : ""}`} ref={dropdownRef}>
      <Search onClick={expandDropdown} onClear={onClearSearch} onChange={handleSearchThrottle} options={[]} placeholder="Search" value={searchText} />
      {console.log("filtered items list ", filteredList)}
      {console.log("Load more list ref ", listRef)}
      <div ref={listRef} className="simpleDropdownList">
        {dropdownExpand && filteredList?.length > 0 ? (
          <>
            {filteredListElement(filteredList)}
            {hasMore && (
              <Flex justify="center">
                <Text color="light" size="p4">
                  Loading more ...{" "}
                </Text>
              </Flex>
            )}
          </>
        ) : (
          dropdownExpand && infoText()
        )}
      </div>

      {dropdownExpand && <div className="dropdownFilterOverlay" onClick={expandDropdown} onKeyDown={(e) => handleKeyDown(e, expandDropdown)} role="button" tabIndex={0} aria-label="Close dropdown" />}
    </div>
  );
}

export default DropdownSimple;
